/**
 * Halftone Image CDN Upload Service (CG-02)
 *
 * Uploads rendered halftone images to Shopify Files API via Cloudflare Worker.
 * Returns GDPR-compliant cdn.shopify.com URLs that can be displayed in checkout.
 *
 * @version 3.0.0 (Customizer v3 - CG-02)
 * @author Claude Code
 */

(function() {
  'use strict';

  const HalftoneImageUploader = {
    /**
     * Get Cloudflare Worker URL from theme settings
     * Fallback to default if not configured
     */
    getWorkerUrl() {
      // Try to get from HalftoneConfig (set in halftone_image.liquid)
      if (window.HalftoneConfig && window.HalftoneConfig.workerUrl) {
        return window.HalftoneConfig.workerUrl;
      }

      // Fallback to default worker URL
      return 'https://halftone-upload.kayatuerkay.workers.dev';
    },

    /**
     * Upload image blob to Shopify Files API via Cloudflare Worker
     * @param {Blob} blob - Image blob from canvas.toBlob()
     * @param {string} filename - Optional filename
     * @returns {Promise<{success: boolean, url: string, fileId?: string, error?: string}>}
     */
    async uploadToCDN(blob, filename) {
      filename = filename || this.generateFilename();
      const workerUrl = this.getWorkerUrl();

      console.log('[Halftone CG-02] Starting Shopify Files upload via Worker...', {
        size: Math.round(blob.size / 1024) + ' KB',
        type: blob.type,
        filename: filename,
        workerUrl: workerUrl
      });

      // Validate worker URL
      if (!workerUrl || workerUrl === '') {
        console.error('[Halftone CG-02] Worker URL not configured in theme settings');
        return {
          success: false,
          url: null,
          error: 'Worker URL not configured',
          needsSetup: true
        };
      }

      try {
        // Convert blob to base64 data URL
        const base64 = await this.blobToBase64(blob);

        // Call Cloudflare Worker with JSON payload
        const response = await fetch(workerUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image: base64,
            filename: filename,
            contentType: blob.type || 'image/jpeg'
          })
        });

        // Parse response
        const result = await response.json();

        if (response.ok && result.success && result.url) {
          console.log('[Halftone CG-02] Shopify Files upload successful:', {
            url: result.url,
            fileId: result.fileId,
            provider: result.provider,
            gdprCompliant: result.gdprCompliant
          });

          return {
            success: true,
            url: result.url,
            fileId: result.fileId,
            provider: result.provider || 'shopify',
            gdprCompliant: result.gdprCompliant || true
          };
        }

        // Upload failed
        console.error('[Halftone CG-02] Worker upload failed:', result.error);
        return {
          success: false,
          url: null,
          error: result.error || 'Upload fehlgeschlagen',
          debug: result.debug
        };

      } catch (error) {
        console.error('[Halftone CG-02] Upload error:', error.message);
        return {
          success: false,
          url: null,
          error: 'Netzwerkfehler beim Upload: ' + error.message
        };
      }
    },

    /**
     * Convert blob to base64 data URL
     * @param {Blob} blob
     * @returns {Promise<string>}
     */
    async blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    /**
     * Generate unique filename for the halftone image
     * @returns {string}
     */
    generateFilename() {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const random = Math.random().toString(36).substring(2, 8);
      return `halftone-${timestamp}-${random}.jpg`;
    }
  };

  // Export to window for use by halftone-customizer.js
  window.HalftoneImageUploader = HalftoneImageUploader;

  console.log('[Halftone CG-02] HalftoneImageUploader initialized (v3.0.0 - Cloudflare Worker)');
})();
